// @flow
import { pick, isBoolean, isNull } from "lodash";
import { isBlank, isString } from "voca";
import $ from "jquery";
import "blueimp-file-upload/js/jquery.fileupload-process";
import "blueimp-file-upload/js/jquery.fileupload-image";
import "blueimp-file-upload/js/jquery.fileupload-validate";
import "cloudinary-jquery-file-upload";
import { widgetParams, getLogger } from "@cld/upload-widget";
import type { UploadField } from "uploader";
import t from "common/i18n";
import appConfig from "appConfig";
import {
	getUploadFieldFormData,
	getUploadFieldFromContainer,
	getAcceptFileTypes,
} from "../common/utils";
import consts from "../common/consts";

const { UPLOAD_PARAMS } = widgetParams;

const logger = getLogger();

const VALID_EMPTY_PARAMS = ["folder", "assetFolder", "publicIdPrefix"];

const getUploadUrl = (options: Object) => {
	let url = options.uploadPrefix || appConfig.api.uploadBase;

	url += `${appConfig.api.uploadVersion}${options.cloudName}/`;

	if (options.resourceType) {
		url += `${options.resourceType}/`;
	}

	return url + appConfig.api.upload;
};

const getMaxChunkSize = (options: Object) => // min: 5,242,880, default: 20,000,000
	Math.max(appConfig.upload.minChunkSize, options.maxChunkSize || appConfig.upload.maxChunkSize);

const getValidCaseParamName = (name: string) => consts.SNAKE_API_SERVER_PARAMS[name] || name;

const getUploadOptions = (options, valueTester) => {
	const uploadOptions = pick(options, UPLOAD_PARAMS);

	return Object.keys(uploadOptions).reduce((res, key) => {
		const val = options[key];

		if (isBoolean(val) || (val && !isBlank(val)) || (VALID_EMPTY_PARAMS.includes(key) && val === "") || valueTester?.(val)) { // wont pass 0...
			res[getValidCaseParamName(key)] = val; // ensure we use snake case for multi-word param names
		}

		return res;
	}, {});
};

const getNullableUploadOptions = (options) => getUploadOptions(options, isNull);

const setErrorMessages = () => ({
		maxNumberOfFiles: t("uploader.errors.max_number_of_files"),
		acceptFileTypes: t("uploader.errors.allowed_formats"),
		maxFileSize: t("uploader.errors.max_file_size"),
		minFileSize: t("uploader.errors.min_file_size"),
	});

const getCloudinaryUploadTag = (fileInput: Object, ddZone: HTMLElement, options: Object, uploadOptions: Object) => {
	logger.log("[uploader.uploadField]: rendering cloudinary upload tag with upload options: ", uploadOptions);

	const uploadUrl = getUploadUrl(options);
	const messages = setErrorMessages();

	return fileInput
		.unsigned_cloudinary_upload(options.uploadPreset, uploadOptions, {
			url: uploadUrl,
			cloud_name: options.cloudName,
			dropZone: $(ddZone),
			multiple: options.multiple,
			acceptFileTypes: getAcceptFileTypes(options.clientAllowedFormats),
			loadImageFileTypes: /^image\/(webp|gif|jpeg|png|svg\+xml)$/,
			autoUpload: false,
			previewCanvas: false,
			minFileSize: 0,
			maxFileSize: 0, // using custom validator for file max size
			maxChunkSize: getMaxChunkSize(options),
			disableImageResize: !(options.maxImageWidth || options.maxImageHeight),
			imageMaxWidth: options.maxImageWidth || (options.maxImageHeight && 0),
			imageMaxHeight: options.maxImageHeight || (options.maxImageWidth && 0),
			imageMinWidth: options.minImageWidth,
			imageMinHeight: options.minImageHeight,
			loadImageMaxFileSize: appConfig.upload.maxImageSize,
			disableImageReferencesDeletion: true,
			previewThumbnail: false,
			disableImagePreview: true,

			limitConcurrentUploads: options.concurrentUploads,

			// used by custom max size processor: ./processors/validateFileMaxSize.js
			customFileMaxSize: options.maxFileSize,
			customImageMaxSize: options.maxImageFileSize,
			customVideoMaxSize: options.maxVideoFileSize,
			customRawMaxSize: options.maxRawFileSize,
			messages,
		});
};

const addSizeProcessing = (uploadField: Object, options: Object) => {
	const processQueue = uploadField.fileupload("option", "processQueue");

	if (options.minImageWidth || options.minImageHeight) {
		processQueue.push({ action: "validateImageMinSize" });
		uploadField.fileupload("option", "processQueue", processQueue);
	}

	if (options.validateMaxWidthHeight) {
		processQueue.push({ action: "validateMaxWidthHeight" });
		uploadField.fileupload("option", "processQueue", processQueue);
	}

	processQueue.push({ action: "validateFileMaxSize" });
	uploadField.fileupload("option", "processQueue", processQueue);
};

const setUploadUrlFromPrepared = (uploadButtonContainer: Object, options: Object, prepared: Object) => {
	const uploadField = getUploadFieldFromContainer(uploadButtonContainer);

	let uploadUrl = uploadField.fileupload("option", "url");

	if (isString(prepared.uploadHost) && !isBlank(prepared.uploadHost)) {
		const newUrl = getUploadUrl({
			uploadPrefix: `//${prepared.uploadHost}/`,
			cloudName: options.cloudName,
		});

		if (uploadUrl !== newUrl) {
			uploadUrl = newUrl;
			logger.log(`[uploader.uploadField]: updating upload url to new one received from prepared data = ${newUrl}`);
			uploadField.fileupload("option", "url", newUrl);
		}
	}

	return uploadUrl;
};

const doUpdatePreparedParams = (uploadButtonContainer: Object, options: Object, prepared: Object) => {
	logger.log("[uploader.uploadField]: about to update form data with prepared params: ", prepared);
	const formData = getUploadFieldFormData(uploadButtonContainer);

	if (prepared.signature) {
		formData.signature = prepared.signature;
		formData.timestamp = prepared.timestamp || formData.timestamp;
	} else {
		delete formData.signature; // cant send empty signature prop even as undefined!
	}

	formData.api_key = prepared.apiKey || options.apiKey;
	formData.upload_preset = prepared.uploadPreset || formData.upload_preset;

	const uploadOptions = getNullableUploadOptions(prepared);

	Object.keys(uploadOptions).forEach((k) => { // use prepared params for form data
		formData[k] = uploadOptions[k];
	});

	// APPS-11022 null value allows us to remove values from form data that might have been left from previous processed file
	Object.keys(formData).forEach((key) => {
		if (isNull(formData[key])) {
			delete formData[key];
		}
	});

	const uploadUrl = setUploadUrlFromPrepared(uploadButtonContainer, options, prepared);

	return { formData, uploadUrl }; // only returning for testing purposes currently
};

const applyFileUpload = (inputElm: Object, ddZone: HTMLElement, options: Object) => {
	const uploadOptions = getUploadOptions(options);

	uploadOptions.timestamp = uploadOptions.uploadSignatureTimestamp;
	delete uploadOptions.uploadSignatureTimestamp; // allow customer to set timestamp along with pre-calculated signature

	const uploadField = getCloudinaryUploadTag(inputElm, ddZone, options, uploadOptions);

	addSizeProcessing(uploadField, options);
};

export default (fieldContainer: HTMLElement, ddZone: HTMLElement, options: Object): UploadField => {
	logger.log("[uploader.uploadField]: initializing upload field with options: ", options);

	let lastOptions = options;

	const uploadButtonContainer = $(fieldContainer);

	const update = (newOpts: Object) => { // public method to initiate a reload of the upload field with new options
		lastOptions = newOpts; // make sure we use the latest options
		const inputElm = getUploadFieldFromContainer(uploadButtonContainer);
		inputElm.fileupload("destroy").removeData(); // clean up the file upload widget before we apply a new one
		applyFileUpload(inputElm, ddZone, newOpts);
	};

	const updatePreparedParams = (params) => // public method to incorporate params returned from the client's prepare callback
		doUpdatePreparedParams(uploadButtonContainer, lastOptions, params);

	applyFileUpload(
		getUploadFieldFromContainer(uploadButtonContainer),
		ddZone,
		lastOptions,
	);

	return {
		update,
		updatePreparedParams,
		container: uploadButtonContainer,
	};
};
